import { useRef, useState, type FormEvent } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, ImageUp, Users, X } from "lucide-react";
import { useOrg, useOrgLogo, useUpdateOrg, useUploadOrgLogo } from "../api/hooks";

// Tela de admin da organização (/orgs/:id): nome, logo e cor primária. A cor
// é aplicada ao vivo quando a org é o espaço ativo (OrgThemeBridge).

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

// Paleta de cores de marca sugeridas.
const PALETTE = ["#066E3E", "#2563eb", "#7c3aed", "#db2777", "#ea580c", "#0891b2", "#4b5563"];

export function OrgAdminRoute() {
  const { orgId } = useParams({ from: "/orgs/$orgId" });
  const id = Number.parseInt(orgId, 10);
  const org = useOrg(id);
  const updateOrg = useUpdateOrg(id);
  const uploadLogo = useUploadOrgLogo(id);
  const logo = useOrgLogo(id, org.data?.hasLogo ?? false);
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState<string | null>(null);

  const canManage = org.data?.myRole === "owner" || org.data?.myRole === "admin";
  const nameValue = name ?? org.data?.name ?? "";

  const saveName = (e: FormEvent) => {
    e.preventDefault();
    const v = nameValue.trim();
    if (v && v !== org.data?.name) updateOrg.mutate({ name: v });
  };

  const pickColor = (color: string | null) => updateOrg.mutate({ primaryColor: color });

  const onLogo = (file: File | null) => {
    if (file) uploadLogo.mutate(file, { onSuccess: () => logo.refetch() });
    if (fileInput.current) fileInput.current.value = "";
  };

  if (org.isLoading) return <p className="px-6 py-6 text-sm text-slate-500 dark:text-slate-400">Carregando…</p>;
  if (!org.data)
    return <p className="px-6 py-6 text-sm text-red-600 dark:text-red-400">Organização não encontrada.</p>;

  return (
    <div className="w-full px-6 py-6">
      <div className="mb-6 flex items-center gap-3">
        <Link
          to="/"
          title="Voltar"
          aria-label="Voltar"
          className="flex h-8 w-8 items-center justify-center rounded text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Configurações</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{org.data.name}</p>
        </div>
        <Link
          to="/orgs/$orgId/members"
          params={{ orgId: String(id) }}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
        >
          <Users className="h-4 w-4" aria-hidden />
          Membros
        </Link>
      </div>

      {!canManage && (
        <p className="mb-4 rounded-md bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          Só admins/owners editam as configurações da organização.
        </p>
      )}

      <div className="max-w-xl space-y-8">
        {/* Nome */}
        <form onSubmit={saveName} className="space-y-2">
          <span className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Nome
          </span>
          <div className="flex gap-2">
            <input value={nameValue} disabled={!canManage} onChange={(e) => setName(e.target.value)} className={inputCls} />
            <button
              type="submit"
              disabled={!canManage || updateOrg.isPending || nameValue.trim() === org.data.name}
              className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:opacity-50"
            >
              Salvar
            </button>
          </div>
        </form>

        {/* Logo */}
        <div className="space-y-2">
          <span className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Logo
          </span>
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
              {org.data.hasLogo && logo.data ? (
                <img src={logo.data} alt="Logo" className="h-full w-full object-contain" />
              ) : (
                <ImageUp className="h-6 w-6 text-slate-300 dark:text-slate-600" aria-hidden />
              )}
            </div>
            {canManage && (
              <div>
                <input
                  ref={fileInput}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => onLogo(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  disabled={uploadLogo.isPending}
                  onClick={() => fileInput.current?.click()}
                  className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-600 transition-colors hover:border-slate-400 hover:text-slate-800 disabled:opacity-50 dark:border-slate-700 dark:text-slate-400 dark:hover:border-slate-500 dark:hover:text-slate-200"
                >
                  {uploadLogo.isPending ? "Enviando…" : "Enviar imagem"}
                </button>
                <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">PNG/JPG/SVG até 2 MB.</p>
              </div>
            )}
          </div>
        </div>

        {/* Cor primária */}
        <div className="space-y-2">
          <span className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Cor primária
          </span>
          <div className="flex flex-wrap items-center gap-2">
            {PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                disabled={!canManage}
                aria-label={`Cor ${c}`}
                aria-pressed={org.data.primaryColor === c}
                onClick={() => pickColor(c)}
                className={[
                  "h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 disabled:opacity-50",
                  org.data.primaryColor === c ? "border-slate-900 dark:border-slate-100" : "border-transparent",
                ].join(" ")}
                style={{ backgroundColor: c }}
              />
            ))}
            {org.data.primaryColor && canManage && (
              <button
                type="button"
                onClick={() => pickColor(null)}
                title="Voltar ao padrão ETUS"
                aria-label="Remover cor personalizada"
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
          <p className="text-xs text-slate-400 dark:text-slate-500">
            A cor é aplicada em toda a interface enquanto você está no espaço desta organização.
          </p>
        </div>
      </div>
    </div>
  );
}
